import { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Table, Modal, Spinner, Alert } from "react-bootstrap";
import { PencilFill, TrashFill, PlusCircleFill } from "react-bootstrap-icons";
import { createClient } from "@supabase/supabase-js";

// Supabase configuration - replace with your actual config
const supabaseUrl = "https://aeauzrauwtlwvhzafpwm.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlYXV6cmF1d3Rsd3ZoemFmcHdtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzkyMjM0OTgsImV4cCI6MjA1NDc5OTQ5OH0.mWw7stDMNG-ceiUqA50bcrkcNxf0HhUtSfzh3nd-t7Y";
const supabase = createClient(supabaseUrl, supabaseKey);

const AdminPage = () => {
  // Authentication states
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Products states
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);

  // Product form states
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    category: "",
    price: 0,
    image: "",
    officialLink: "",
    specs: [{ label: "", value: "" }]
  });
  const [imageFile, setImageFile] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Check auth status on load
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const currentUser = session?.user || null;
        setUser(currentUser);
        if (currentUser) {
          console.log("User is logged in:", currentUser.email);
          fetchProducts();
        }
      }
    );

    // Initial check for existing session
    checkUser();

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Check if user is already logged in
  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const currentUser = session?.user || null;
    setUser(currentUser);
    if (currentUser) {
      fetchProducts();
    }
  };

  // Fetch products from Supabase
  const fetchProducts = async () => {
    try {
      setProductLoading(true);
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });
        console.log(data);

      if (error) throw error;
      setProducts(data || []);
      setProductError(null);     
    } catch (err) {
      console.error("Error fetching products:", err);
      setProductError("Failed to load products. Please try again later.");
    } finally {
      setProductLoading(false);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setAuthError(null);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      setEmail("");
      setPassword("");
    } catch (err) {
      console.error("Login error:", err);
      setAuthError("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "price" ? Number(value) : value
    });
  };

  // Handle spec changes
  const handleSpecChange = (index, field, value) => {
    const updatedSpecs = [...formData.specs];
    updatedSpecs[index] = {
      ...updatedSpecs[index],
      [field]: value
    };
    setFormData({
      ...formData,
      specs: updatedSpecs
    });
  };

  // Add new spec field
  const addSpecField = () => {
    setFormData({
      ...formData,
      specs: [...formData.specs, { label: "", value: "" }]
    });
  };

  // Remove spec field
  const removeSpecField = (index) => {
    const updatedSpecs = [...formData.specs];
    updatedSpecs.splice(index, 1);
    setFormData({
      ...formData,
      specs: updatedSpecs
    });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      brand: "",
      category: "",
      price: 0,
      image: "",
      officialLink: "",
      specs: [{ label: "", value: "" }]
    });
    setImageFile(null);
    setEditingProduct(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  // Open modal for adding new product
  const handleAddProduct = () => {
    resetForm();
    setShowProductModal(true);
  };

  // Open modal for editing product
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand,
      category: product.category,
      price: product.price,
      image: product.image,
      officialLink: product.officialLink || "",
      specs: product.specs || [{ label: "", value: "" }]
    });
    setShowProductModal(true);
  };

  // Upload image to Supabase Storage
  const uploadImage = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `product_images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('products')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image: ', error);
      throw error;
    }
  };

  // Save product (add or update)
  const handleSaveProduct = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);
      setSaveSuccess(false);

      let imageUrl = formData.image;

      // Upload image if a new file is selected
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const productData = {
        name: formData.name,
        brand: formData.brand,
        category: formData.category,
        price: Number(formData.price),
        image: imageUrl,
        officialLink: formData.officialLink,
        specs: formData.specs,
        updated_at: new Date()
      };

      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);

        if (error) throw error;
      } else {
        // Add new product
        productData.created_at = new Date();
        const { error } = await supabase
          .from('products')
          .insert([productData]);

        if (error) throw error;
      }

      setSaveSuccess(true);
      fetchProducts();
      
      // Close modal after a brief delay to show success message
      setTimeout(() => {
        setShowProductModal(false);
        resetForm();
      }, 1500);
    } catch (err) {
      console.error("Error saving product:", err);
      setSaveError("Failed to save product. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        setProductLoading(true);
        
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', productId);

        if (error) throw error;
        
        await fetchProducts();
      } catch (err) {
        console.error("Error deleting product:", err);
        setProductError("Failed to delete product. Please try again.");
      } finally {
        setProductLoading(false);
      }
    }
  };

  // Render login form
  if (!user) {
    return (
      <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6}>
        <Card className="shadow">
          <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Entrez vos identifiants</h4>
          </Card.Header>
          <Card.Body>
          <Form onSubmit={handleLogin}>
            {authError && (
            <Alert variant="danger">{authError}</Alert>
            )}
            <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            </Form.Group>
            <Form.Group className="mb-3">
            <Form.Label>Mot de passe</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            </Form.Group>
            <Button 
            type="submit" 
            variant="primary" 
            className="w-100"
            disabled={loading}
            >
            {loading ? (
              <>
              <Spinner animation="border" size="sm" className="me-2" />
              Connexion...
              </>
            ) : "Se connecter"}
            </Button>
          </Form>
          </Card.Body>
        </Card>
        </Col>
      </Row>
      </Container>
    );
  }

  // Render admin dashboard
  return (
    <Container fluid className="py-5 px-3">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">Product Management</h2>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" onClick={handleLogout} className="me-2">
            Logout
          </Button>
          <Button variant="primary" onClick={handleAddProduct}>
            <PlusCircleFill className="me-2" />
            Add Product
          </Button>
        </Col>
      </Row>

      {productError && (
        <Alert variant="danger">{productError}</Alert>
      )}

      {productLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading products...</p>
        </div>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td width="80">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="img-fluid" 
                        style={{ width: "60px", height: "60px", objectFit: "contain" }} 
                      />
                    </td>
                    <td>{product.name}</td>
                    <td>{product.brand}</td>
                    <td>{product.category}</td>
                    <td>{product.price.toLocaleString("fr-FR")} DA</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleEditProduct(product)}
                      >
                        <PencilFill />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        <TrashFill />
                      </Button>
                    </td>
                  </tr>
                ))}

                {products.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No products found. Click "Add Product" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Product Add/Edit Modal */}
      <Modal 
        show={showProductModal} 
        onHide={() => setShowProductModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProduct ? "Edit Product" : "Add New Product"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && (
            <Alert variant="danger">{saveError}</Alert>
          )}

          {saveSuccess && (
            <Alert variant="success">
              Product successfully {editingProduct ? "updated" : "added"}!
            </Alert>
          )}

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Serveurs">Serveurs</option>
                    <option value="Stations">Stations</option>
                    <option value="Baie de stockages">Baie de stockages</option>
                    <option value="PC de Bureau">PC de Bureau</option>
                    <option value="PC Portables">PC Portables</option>
                    <option value="Réseaux">Réseaux</option>
                    <option value="Imprimantes">Imprimantes</option>
                    <option value="Logiciels">Logiciels</option>
                    <option value="Accessoires">Accessoires</option>
                    <option value="Armoires">Armoires</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Price (DA)</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Official Link</Form.Label>
                  <Form.Control
                    type="url"
                    name="officialLink"
                    value={formData.officialLink}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Product Image</Form.Label>
                  {formData.image && (
                    <div className="mb-2">
                      <img 
                        src={formData.image} 
                        alt="Product Preview" 
                        className="img-thumbnail mb-2" 
                        style={{ height: "100px" }} 
                      />
                    </div>
                  )}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  <Form.Text className="text-muted">
                    {formData.image ? "Upload a new image to replace the current one" : "Upload product image"}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Product Specifications</h5>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={addSpecField}
                >
                  + Add Spec
                </Button>
              </div>

              {formData.specs.map((spec, index) => (
                <Row key={index} className="mb-2">
                  <Col>
                    <Form.Control
                      placeholder="Label (e.g., CPU)"
                      value={spec.label}
                      onChange={(e) => handleSpecChange(index, "label", e.target.value)}
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      placeholder="Value (e.g., Intel i7)"
                      value={spec.value}
                      onChange={(e) => handleSpecChange(index, "value", e.target.value)}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => removeSpecField(index)}
                      disabled={formData.specs.length <= 1}
                    >
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProductModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveProduct}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : "Save Product"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPage;